import React, { useState } from "react"
import { Box, Button, Tab, Tabs, TextField, Typography } from "@mui/material";
import FilterListIcon from '@mui/icons-material/FilterAltOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import filterSectionStyle from "../../styles/filterSectionStyle";
import MonthYearSelector, { MonthYearSelectorPlaceholder } from "./monthYearSelector";
import RoleManagement from "../../security/roleManagement";
import MenuComponent, { MenuObject } from "./menuComponent";
import UploadComponent from "./uploadComponent";
import JWTTokenPayload from "../../interfaces/JWTTokenPayload";
import { currentPoolMonth, decodedJWT, getOwnerType, getUserRole } from "../../utils/commonUtils";
import { bulkFieldUpdateFileFormat, removeAgentAllocationFileFormat, uploadAgentAllocationFileFormat, uploadCaseFileFormat, uploadFocusedCaseAllocationByAgency, uploadFocusedCaseAllocationByClient, uploadPaymentFileFormat } from "../../constants/columnFormats";
import Filter from "../../interfaces/filters";
import BulkDownloadModal from "../modals/bulkDownloadModal";
import UploadCaseFileComponent from "./uploadCaseFileComponent";

interface ToolbarContainerProps {
    setFilters: React.Dispatch<React.SetStateAction<Filter[]>>;
    toggleDrawer: () => void;
    filtersCount: number;
    universalSearchQuery: string;
    setUniversalSearchQuery: React.Dispatch<React.SetStateAction<string>>;
    selectedPoolMonth: string;
    setSelectedPoolMonth: React.Dispatch<React.SetStateAction<string>>;
    resetFilters: () => void;
};

const ToolbarContainer: React.FC<ToolbarContainerProps> = ({
    setFilters, toggleDrawer, filtersCount, universalSearchQuery, setUniversalSearchQuery, selectedPoolMonth, setSelectedPoolMonth,
    resetFilters
}) => {
    const token: JWTTokenPayload | false = decodedJWT();
    const userRole = getUserRole(token);
    const ownerType = getOwnerType(token);
    const [bulkDownloadModalVisible, setBulkDownloadModalVisible] = useState<boolean>(false);
    const [uploadCaseModal, setUploadCaseModal] = useState<boolean>(false);
    const [uploadPaymentModal, setUploadPaymentModal] = useState<boolean>(false);
    const [bulkUpdateModal, setBulkUpdateModal] = useState<boolean>(false);
    const [uploadAgentAllocationModal, setUploadAgentAllocationModal] = useState<boolean>(false);
    const [removeAgentAllocationModal, setRemoveAgentAllocationModal] = useState<boolean>(false);
    const [focusedCaseAllocationModal, setFocusedCaseAllocationModal] = useState<boolean>(false);
    const [focusedCaseDeallocationModal, setFocusedCaseDeallocationModal] = useState<boolean>(false);
    const [quickFilterValue, setQuickFilterValue] = useState<string | false>(false);
    const openModal = () => setBulkDownloadModalVisible(true);
    const hideModal = () => setBulkDownloadModalVisible(false);

    const handleUniversalSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
        setUniversalSearchQuery(event.target.value);
    }

    const handleResetFilters = () => {
        resetFilters();
		setSelectedPoolMonth(currentPoolMonth());
		setQuickFilterValue('');
	};

    const handleQuickFilterChange = (newValue: string) => {
        setQuickFilterValue(prevValue => {
            // If the user clicks on the already selected tab, reset the selection
            if (prevValue === newValue) {
                setFilters(prevFilters =>
                    prevFilters.map(filter => {
                        if (filter.key === 'status') {
                            return {
                                ...filter,
                                value: []
                            };
                        }
                        return filter;
                    })
                );
                return ""; // Reset selection (instead of false)
            }

            // Otherwise, apply the new filter
            setFilters(prevFilters =>
                prevFilters.map(filter => {
                    if (filter.key === 'status') {
                        return {
                            ...filter,
                            value: newValue === 'New' ? ['New'] :
                                newValue === 'Normalize' ? ['Normalize'] :
                                newValue === 'Partially Resolved' ? ['Roll Back', 'Stabilize']:
                                newValue === 'In Progress'?['In Progress'] :[]
                        };
                    }
                    return filter;
                })
            );

            return newValue; // Set new tab selection
        });
    };

    const menuOptions: MenuObject[] = [
		{
			text: 'Upload Case File',
			onPress: () => setUploadCaseModal(true),
			dividerNext: true,
			showToUser: RoleManagement.isAdmin(userRole) && RoleManagement.isClient(ownerType),
		},
		{
			text: 'Upload Payment File',
			onPress: () => setUploadPaymentModal(true),
			dividerNext: true,
			showToUser: RoleManagement.isAdmin(userRole) && RoleManagement.isClient(ownerType),
		},
		{
			text: 'Bulk Update Field',
			onPress: () => setBulkUpdateModal(true),
			dividerNext: true,
			showToUser: RoleManagement.isAdmin(userRole) && RoleManagement.isClient(ownerType),
		},
        {
			text: 'Bulk Download',
			onPress: openModal,
            dividerNext: true,
			showToUser: !RoleManagement.isAgent(userRole),
		},
        {
            text: 'Upload Agent Allocation File',
            onPress: () => setUploadAgentAllocationModal(true),
            showToUser: RoleManagement.isAgency(ownerType) && (RoleManagement.isSupervisor(userRole) || RoleManagement.isAdmin(userRole)),
            dividerNext: true,
        },
        {
            text: 'Remove Agent Allocation File',
            onPress: () => setRemoveAgentAllocationModal(true),
            showToUser: RoleManagement.isAgency(ownerType) && (RoleManagement.isSupervisor(userRole) || RoleManagement.isAdmin(userRole)),
            dividerNext: true,
        },
        {
            text: 'Focused Case Allocation File',
            onPress: () => setFocusedCaseAllocationModal(true),
            showToUser: !RoleManagement.isAgent(userRole),
            dividerNext: true,
        },
        {
            text: 'Focused Case Deallocation File',
            onPress: () => setFocusedCaseDeallocationModal(true),
            showToUser: !RoleManagement.isAgent(userRole),
        },
	];

    const buttonComponent: React.FC = () => {
		return (
			<Box sx={{ border: '1px solid #1976D2', borderRadius: 1, p: '5px 15px', mt: 0.5, justifyContent: 'center' }}>
				<Typography variant="subtitle2" sx={{ color: '#1976D2' }}>Actions</Typography>
			</Box>
		);
	};

    return (
        <>
            <Box sx={filterSectionStyle} display="flex" flexDirection='column'>
				<Box display="flex" justifyContent={'space-between'}>
					<Box sx={{ display: 'flex', alignItems: 'center' }}>
						<Button
							variant={filtersCount === 0 ? "outlined" : "contained"}
							color="primary"
							startIcon={<FilterListIcon fontSize="small" />}
							onClick={toggleDrawer}
							aria-label="open drawer"
						>
							<Typography variant="subtitle2">Filters [{filtersCount}]</Typography>
						</Button>
						<Button style={{ marginLeft: "10px", marginRight: "10px" }}
							variant="outlined"
							color="primary"
							startIcon={<RefreshIcon fontSize="small" />}
							onClick={() => {
								handleResetFilters();
							}}
							aria-label="Refresh"
						>
							<Typography variant="subtitle2">Reset</Typography>
						</Button>
						{RoleManagement.isAgency(ownerType) ? (
							<MonthYearSelectorPlaceholder />
						) : (
							<MonthYearSelector keys={selectedPoolMonth} onDateSelection={setSelectedPoolMonth} />
						)}
						<TextField
							value={universalSearchQuery}
							onChange={handleUniversalSearch}
							onInput={handleUniversalSearch}
							placeholder="Search..."
							variant="outlined"
							slotProps={{ input: { sx: {
							    fontSize: "14px",
							    padding: "0px",
							    margin: "8px",
							    minWidth: "600px",
							    maxHeight: "36px"
							}}}}
						/>
					</Box>
                    <Box display="flex" justifyContent="flex-end">
                        <MenuComponent options={menuOptions} menuAnchorElement={buttonComponent} />
                        <UploadCaseFileComponent
                            uploadFormat={uploadCaseFileFormat}
                            label="Upload Case File"
                            uploadApi="/api/v1/case/upload"
                            uploadType='CaseUpload'
                            open={uploadCaseModal}
                            onClose={() => setUploadCaseModal(false)}
                        />
                        <UploadComponent
                            uploadFormat={uploadPaymentFileFormat}
                            label="Upload Payment File"
                            uploadApi="/api/v1/reconcile/upload"
                            uploadType='PaymentFile'
                            open={uploadPaymentModal}
                            onClose={() => setUploadPaymentModal(false)}
                        />
                        <UploadCaseFileComponent
                            uploadFormat={bulkFieldUpdateFileFormat}
                            label="Bulk Update Field"
                            uploadApi="/api/v1/case/bulk_field_update"
                            uploadType='BulkUpdate'
                            open={bulkUpdateModal}
                            onClose={() => setBulkUpdateModal(false)}
                        />
                        <UploadComponent
                            uploadFormat={uploadAgentAllocationFileFormat}
                            label="Upload Agent Allocation File"
                            uploadApi="/api/v1/agent_case_allocation/upload_agent_allocation_file"
                            uploadType='PaymentFile'
                            open={uploadAgentAllocationModal}
                            onClose={() => setUploadAgentAllocationModal(false)}
                        />
                        <UploadComponent
                            uploadFormat={removeAgentAllocationFileFormat}
                            label="Remove Agent Allocation File"
                            uploadApi="/api/v1/agent_case_allocation/remove_allocation_file"
                            uploadType='PaymentFile'
                            open={removeAgentAllocationModal}
                            onClose={() => setRemoveAgentAllocationModal(false)}
                        />
                        <UploadComponent
                            uploadFormat={RoleManagement.isAgency(ownerType) ? uploadFocusedCaseAllocationByAgency : uploadFocusedCaseAllocationByClient}
                            label="Focused Case Allocation"
                            uploadApi="/api/v1/case/focused_case_allocation_upload"
                            uploadType='Focused Case File'
                            open={focusedCaseAllocationModal}
                            onClose={() => setFocusedCaseAllocationModal(false)}
                        />
                        <UploadComponent
                            uploadFormat={RoleManagement.isAgency(ownerType) ? uploadFocusedCaseAllocationByAgency : uploadFocusedCaseAllocationByClient}
                            label="Focused Case Deallocation"
                            uploadApi="/api/v1/case/focused_case_deallocation_upload"
                            uploadType='Focused Case File'
                            open={focusedCaseDeallocationModal}
                            onClose={() => setFocusedCaseDeallocationModal(false)}
                        />
                    </Box>
				</Box>
			</Box>
			<Box sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                <Tabs value={quickFilterValue} >
                    <Tab label="Yet To Attempt" value="New" onClick={() => handleQuickFilterChange("New")} />
                    <Tab label="In Progress" value="In Progress" onClick={() => handleQuickFilterChange("In Progress")} />
                    <Tab label="Partially Resolved" value="Partially Resolved" onClick={() => handleQuickFilterChange("Partially Resolved")} />
                    <Tab label="Normalized" value="Normalize" onClick={() => handleQuickFilterChange("Normalize")} />
                </Tabs>
            </Box>
            <BulkDownloadModal modalVisible={bulkDownloadModalVisible} hideModal={hideModal} />
        </>
    );
};

export default ToolbarContainer;